import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AlertTriangleIcon, ChevronRightIcon } from 'lucide-react';
import { Breadcrumbs } from '../components/layout/Breadcrumbs';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { useAppState } from '../contexts/AppState';
import { officers } from '../data/reporting';
import { formatAge, formatDuration, formatRelative, requiredCallsFor } from '../utils/format';
const th = 'px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-ink-400';
const td = 'px-4 py-4 text-sm text-ink-700';
export function LeadList() {
  const {
    officerId,
    sourceId
  } = useParams();
  const navigate = useNavigate();
  const {
    allLeads,
    publishedSources,
    publishedPriority
  } = useAppState();
  const officer = officers.find(o => o.id === officerId);
  const source = publishedSources.find(s => s.id === sourceId);
  const enabledPriority = publishedPriority.filter(r => r.enabled);
  const rows = allLeads.filter(lead => lead.sourceId === sourceId);
  return <div className="space-y-5" data-id="element-400" data-mp="175wpqurfrp9s">
      <Breadcrumbs items={[{
      label: 'Management Dashboard',
      to: '/reporting'
    }, ...(officer ? [{
      label: officer.name,
      to: `/reporting/officer/${officer.id}`
    }] : [{
      label: 'All Loan Officers'
    }]), {
      label: source?.name ?? 'Lead Source'
    }]} data-id="element-401" data-mp="1b9mf3mfxqavv" />

      <div data-id="element-402" data-mp="niyvi3wnpwth">
        <h1 className="text-lg font-semibold tracking-tight text-ink-900" data-id="element-403" data-mp="nih0falqdon5">
          {source?.name} Leads{officer ? ` · ${officer.name}` : ''}
        </h1>
        <p className="mt-0.5 text-sm text-ink-500" data-id="element-404" data-mp="ejls9sz4m4h7">
          Leads governed by the published cadence for this source. Select a lead to inspect its full
          history.
        </p>
      </div>

      <Card className="overflow-hidden" data-id="element-405" data-mp="4ke6qii8n3j0">
        <div className="overflow-x-auto" data-id="element-406" data-mp="4lft3j4pfcg1">
          <table className="w-full min-w-[1040px]" data-id="element-407" data-mp="9jppty62ul3r">
            <thead className="border-b border-ink-200 bg-ink-50/60" data-id="element-408" data-mp="9jhcy69yffsk">
              <tr data-id="element-409" data-mp="3ml06hcapfyv4">
                <th scope="col" className={th} data-id="element-410" data-mp="3mljrj1vnj3dd">
                  Lead
                </th>
                <th scope="col" className={th} data-id="element-411" data-mp="3ht9tszoaoz0j">
                  Lead Source
                </th>
                <th scope="col" className={th} data-id="element-412" data-mp="3cvqgq33c2sa6">
                  Lead Age
                </th>
                <th scope="col" className={th} data-id="element-413" data-mp="37wx1a54hkr7d">
                  Calls Required
                </th>
                <th scope="col" className={th} data-id="element-414" data-mp="9cx69z5uqjk8">
                  Calls Completed
                </th>
                <th scope="col" className={th} data-id="element-415" data-mp="4gghd2qtw163">
                  Last Activity
                </th>
                <th scope="col" className={th} data-id="element-416" data-mp="3vk548gefzo68">
                  Past Due Activity
                </th>
                <th scope="col" className={th} data-id="element-417" data-mp="3qltvya3splq4">
                  Priority
                </th>
                <th scope="col" className={th} data-id="element-418" data-mp="2k72ti1z0xstz">
                  Loan Officer
                </th>
                <th scope="col" className={th} aria-label="Open" data-id="element-419" data-mp="2f5n45xptd05s" />
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-200" data-id="element-420" data-mp="uqqr9og3s3gt">
              {rows.map(lead => {
              const required = requiredCallsFor(source, lead.ageMinutes);
              const overdue = lead.activities.filter(a => a.dueInMinutes !== undefined && a.dueInMinutes < 0);
              const rank = enabledPriority.findIndex(r => r.id === lead.priorityRuleId) + 1;
              const ownerName = officers.find(o => o.id === lead.ownerId)?.name ?? officer?.name ?? '—';
              return <tr key={lead.id} tabIndex={0} role="link" onClick={() => navigate(`/reporting/lead/${lead.id}`)} onKeyDown={e => {
                if (e.key === 'Enter') navigate(`/reporting/lead/${lead.id}`);
              }} className="cursor-pointer transition-colors hover:bg-ink-50 focus:bg-ink-50 focus:outline-none" data-id="element-421" data-mp="1ndjucdgytetz">
                    <td className={`${td} font-medium text-ink-900`} data-id="element-422" data-mp="1jh5x72zqmqmg">{lead.name}</td>
                    <td className={td} data-id="element-423" data-mp="1xgou0zn3mfjg">{source?.name}</td>
                    <td className={`${td} tabular`} data-id="element-424" data-mp="1xg5cvl59vv6h">{formatAge(lead.ageMinutes)}</td>
                    <td className={`${td} tabular`} data-id="element-425" data-mp="1tg4vpsqsh5vq">{required}</td>
                    <td className={`${td} tabular`} data-id="element-426" data-mp="15p98za16x0vc">{lead.callsToday}</td>
                    <td className={`${td} tabular`} data-id="element-427" data-mp="15n4xo70yozl1">
                      {lead.lastContactMinutes !== undefined ? formatRelative(lead.lastContactMinutes) : 'None'}
                    </td>
                    <td className={td} data-id="element-428" data-mp="19quj4nmcb11u">
                      {overdue.length > 0 ? <Badge tone="warning" icon={<AlertTriangleIcon className="h-3 w-3" aria-hidden="true" data-id="element-430" data-mp="hzwsrq7g3sjj" />} data-id="element-429" data-mp="m107g9drbij6">
                          {formatDuration(overdue[0].dueInMinutes ?? 0)} late
                        </Badge> : <span className="text-ink-400" data-id="element-431" data-mp="cnhyxagxrgiy">On track</span>}
                    </td>
                    <td className={td} data-id="element-432" data-mp="3u515ortexkw7">
                      <Badge tone="neutral" data-id="element-433" data-mp="2wnabzg7w4uc">{rank > 0 ? `#${rank}` : 'Not queued'}</Badge>
                    </td>
                    <td className={td} data-id="element-434" data-mp="qly6f3mmhb25">{ownerName}</td>
                    <td className={`${td} text-right`} data-id="element-435" data-mp="qkem91xuifv3">
                      <ChevronRightIcon className="h-4 w-4 text-ink-300" aria-hidden="true" data-id="element-436" data-mp="vkj4oxibdkny" />
                    </td>
                  </tr>;
            })}
              {rows.length === 0 ? <tr data-id="element-437" data-mp="lm3cer7jw5cv">
                  <td colSpan={10} className="px-4 py-14 text-center text-sm text-ink-500" data-id="element-438" data-mp="lljw1exjtp0y">
                    No leads from this source are currently governed by the published configuration.
                  </td>
                </tr> : null}
            </tbody>
          </table>
        </div>
      </Card>
    </div>;
}